import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box, Flex, IconButton, Button, Input, Image, Table, Thead, Tbody, Tr, Th, Td,
  Text, useToast, FormControl, FormLabel,
} from "@chakra-ui/react";
import { FaArrowLeft, FaPlus, FaImage } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Panel } from "../../../components/shared/Section";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import { buildLogoCandidates, nextLogoCandidate, uploadSupplierLogo, validateLogoFile, LOGO_ACCEPT } from "../../../tools/supplierLogo";

const API = process.env.REACT_APP_API_URL;
const RETAILER_FLAGS = ["pnp", "chk", "dis", "mak", "cli", "med", "pet"];

// Same at-a-glance state chip pattern as the Users table.
const FlagChip = ({ on, label, onClick }) => (
  <Button
    size="xs"
    variant="ghost"
    onClick={onClick}
    aria-pressed={on}
    minW="40px"
    fontWeight="600"
    color={on ? "signal.up" : "ink.400"}
    bg={on ? "signal.upSoft" : "ink.100"}
    _hover={{ bg: on ? "signal.upSoft" : "ink.200" }}
  >
    {label}
  </Button>
);

/**
 * Organisations admin (master only). Lists organisations with click-to-toggle
 * retailer entitlements + active flag, and creates new ones. Assigning
 * users/suppliers to an organisation happens on Add User via the org toggle.
 */
const Organisations = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { fetchOrganisations } = useSupplierRetailer();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOrg, setNewOrg] = useState({ org_code: "", org_name: "", contact_email: "" });
  const [newFlags, setNewFlags] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [logoBust, setLogoBust] = useState(0); // bump after upload so the new image shows
  const fileInputRef = useRef(null);
  const [logoTarget, setLogoTarget] = useState(null); // org_code awaiting a file pick

  // Org logos live in the supplier-logo store under ORG_<code> — no collisions
  // with real supplier codes, and the whole upload/serve pipeline is reused.
  const pickLogo = (org_code) => {
    setLogoTarget(org_code);
    fileInputRef.current?.click();
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !logoTarget) return;
    const problem = validateLogoFile(file);
    if (problem) {
      toast({ title: "Logo not accepted", description: problem, status: "warning", duration: 5000, isClosable: true });
      return;
    }
    try {
      await uploadSupplierLogo(file, `ORG_${logoTarget}`);
      setLogoBust((n) => n + 1);
      toast({ title: "Logo uploaded", description: `${logoTarget} logo updated.`, status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: "Logo upload failed", description: error?.response?.data?.message, status: "error", duration: 6000, isClosable: true });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/get-organisations`, {});
      setOrgs(res.data?.data || []);
    } catch (error) {
      toast({ title: "Failed to load organisations", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrg = async (org) => {
    try {
      const retailers = RETAILER_FLAGS.reduce((acc, f) => ({ ...acc, [f]: !!org[f] }), {});
      await axios.post(`${API}/admin/upd-organisation`, {
        org_id: org.org_id,
        org_name: org.org_name,
        contact_email: org.contact_email,
        retailers,
        activeyn: org.activeyn,
      });
      fetchOrganisations(); // keep the top-bar toggle in step
    } catch (error) {
      toast({ title: "Failed to update organisation", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
      load(); // resync after a failed optimistic toggle
    }
  };

  const toggleFlag = (org, flag) => {
    const updated = { ...org, [flag]: !org[flag] };
    setOrgs((prev) => prev.map((o) => (o.org_id === org.org_id ? updated : o)));
    saveOrg(updated);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/ins-organisation`, {
        org_code: newOrg.org_code,
        org_name: newOrg.org_name,
        contact_email: newOrg.contact_email,
        retailers: newFlags,
      });
      toast({ title: res.data.message, status: "success", duration: 3000, isClosable: true });
      setNewOrg({ org_code: "", org_name: "", contact_email: "" });
      setNewFlags({});
      load();
      fetchOrganisations();
    } catch (error) {
      toast({ title: "Failed to create organisation", description: error?.response?.data?.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <input ref={fileInputRef} type="file" accept={LOGO_ACCEPT} style={{ display: "none" }} onChange={handleLogoFile} />
      <Flex mb={4} align="center" gap={3}>
        <IconButton icon={<FaArrowLeft />} colorScheme="teal" onClick={() => navigate(-1)} aria-label="Go back" />
        <Text fontSize="sm" color="ink.500">
          Retailer chips control what the organisation is entitled to see — click to toggle.
        </Text>
      </Flex>

      <Panel padded={false} maxH="50vh" overflow="auto" mb={6}>
        {loading ? (
          <TableSkeleton rows={4} minH="160px" />
        ) : (
          <Table variant="striped" size="sm" colorScheme="custom">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th px={2} py={1} bg="gray.50">Logo</Th>
                <Th px={2} py={1} bg="gray.50">Code</Th>
                <Th px={2} py={1} bg="gray.50">Name</Th>
                <Th px={2} py={1} bg="gray.50">Contact email</Th>
                <Th px={2} py={1} bg="gray.50">Retailers</Th>
                <Th px={2} py={1} bg="gray.50">Active</Th>
              </Tr>
            </Thead>
            <Tbody>
              {orgs.map((org) => {
                const candidates = buildLogoCandidates(`ORG_${org.org_code}`, { cacheBust: logoBust || undefined, preferApi: logoBust > 0 });
                return (
                <Tr key={org.org_id}>
                  <Td px={2} py={1}>
                    <Flex align="center" gap={1}>
                      <Image
                        key={`${org.org_code}-${logoBust}`}
                        src={candidates[0]}
                        onError={({ currentTarget }) => nextLogoCandidate(currentTarget, candidates)}
                        alt=""
                        boxSize="26px"
                        borderRadius="4px"
                        objectFit="contain"
                        border="1px solid"
                        borderColor="gray.200"
                        bg="white"
                      />
                      <IconButton icon={<FaImage />} size="xs" variant="ghost" aria-label="Upload logo" title="Upload logo" onClick={() => pickLogo(org.org_code)} />
                    </Flex>
                  </Td>
                  <Td px={2} py={1} fontWeight="600">{org.org_code}</Td>
                  <Td px={2} py={1}>{org.org_name}</Td>
                  <Td px={2} py={1}>{org.contact_email || "-"}</Td>
                  <Td px={2} py={1}>
                    <Flex gap={1} wrap="wrap">
                      {RETAILER_FLAGS.map((f) => (
                        <FlagChip key={f} on={!!org[f]} label={f.toUpperCase()} onClick={() => toggleFlag(org, f)} />
                      ))}
                    </Flex>
                  </Td>
                  <Td px={2} py={1}>
                    <FlagChip on={!!org.activeyn} label={org.activeyn ? "Yes" : "No"} onClick={() => toggleFlag(org, "activeyn")} />
                  </Td>
                </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Panel>

      <Panel maxW="560px">
        <form onSubmit={handleCreate}>
          <Text fontWeight="600" mb={3}>Add organisation</Text>
          <FormControl isRequired mb={2}>
            <FormLabel fontSize="sm">Code</FormLabel>
            <Input
              value={newOrg.org_code}
              onChange={(e) => setNewOrg({ ...newOrg, org_code: e.target.value.toUpperCase() })}
              placeholder="ie: GATEWAY"
            />
          </FormControl>
          <FormControl isRequired mb={2}>
            <FormLabel fontSize="sm">Name</FormLabel>
            <Input
              value={newOrg.org_name}
              onChange={(e) => setNewOrg({ ...newOrg, org_name: e.target.value })}
              placeholder="ie: Gateway"
            />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Contact email</FormLabel>
            <Input
              type="email"
              value={newOrg.contact_email}
              onChange={(e) => setNewOrg({ ...newOrg, contact_email: e.target.value })}
              placeholder="optional"
            />
          </FormControl>
          <FormControl mb={4}>
            <FormLabel fontSize="sm">Retailers</FormLabel>
            <Flex gap={1} wrap="wrap">
              {RETAILER_FLAGS.map((f) => (
                <FlagChip
                  key={f}
                  on={!!newFlags[f]}
                  label={f.toUpperCase()}
                  onClick={() => setNewFlags((prev) => ({ ...prev, [f]: !prev[f] }))}
                />
              ))}
            </Flex>
          </FormControl>
          <Button type="submit" leftIcon={<FaPlus />} isLoading={submitting}>
            Create organisation
          </Button>
        </form>
      </Panel>
    </Box>
  );
};

export default Organisations;
